import { useRef, useState } from 'react'

import { Box, Button, Checkbox, Text } from '@factorialco/deprecated-design-system'
import { RichTextEditorHandle } from '@factorialco/f0-react/dist/experimental'
import {
  AtsGetJobPostingSettingsSmartActionsDocument,
  AtsJobPostingSettingsEditSmartActionFragment,
  AtsUpdateCandidateEmailActionDocument,
  AtsUpdateCandidateEmailActionMutation,
} from 'generated/resources/graphql'
import { difference, isEmpty, pickBy, xor } from 'lodash'
import { useToggle } from 'usehooks-ts'

import SelectField from 'modules/core/components/SelectField'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { TextEditorWithMessageTemplates } from 'modules/ats/components/TextEditorWithMessageTemplates'
import { useMessageOptions } from 'modules/ats/lib/utils/messages'
import { ApplicationPhasesSelector } from 'modules/ats/pages/JobPostingSettings/SmartActions/ApplicationPhasesSelector'
import { MissingFieldsAlert } from 'modules/ats/pages/JobPostingSettings/SmartActions/MissingFieldsAlert'

import { FormValues } from './forms'

interface Props {
  open: boolean
  onClose: () => void
  selectedSmartAction: AtsJobPostingSettingsEditSmartActionFragment
  smartActions: AtsJobPostingSettingsEditSmartActionFragment[]
}

export const EditSmartAction = ({ open, onClose, selectedSmartAction, smartActions }: Props) => {
  const { defaultOption, fallbackOption, sendMessageOptions } = useMessageOptions()
  const [filesToRemove, setFilesToRemove] = useState<string[]>([])
  const richTextEditorRef = useRef<RichTextEditorHandle>(null)

  const [updateSmartAction, { loading }] = useMutation(AtsUpdateCandidateEmailActionDocument, {
    extractorFn: (data: AtsUpdateCandidateEmailActionMutation) =>
      data.atsMutations.updateAtsCandidateEmailAction,
    refetchQueries: [AtsGetJobPostingSettingsSmartActionsDocument],
  })

  const currentFiles = useMemo(() => {
    return selectedSmartAction.files.map(
      (atsFile) =>
        new File([atsFile.file.url], atsFile.file.filename, {
          type: atsFile.file.contentType,
        })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSmartAction.files.length])

  const form = useForm<FormValues>({
    defaultValues: {
      id: selectedSmartAction.id,
      messageContent: selectedSmartAction.messageContent,
      delay: selectedSmartAction.delay,
      emailFrom: selectedSmartAction.emailFrom,
      atsApplicationPhaseIds: selectedSmartAction.atsApplicationPhases.map((phase) => phase.id),
      files: [],
      deleteFiles: [],
    },
    validate: (data) => {
      const formErrors: FormErrors<FormValues> = {}

      if (data.atsApplicationPhaseIds.length === 0) {
        formErrors.atsApplicationPhaseIds = i18n.t('common.validations.empty')
      }

      if (!data.messageContent) {
        formErrors.messageContent = i18n.t('common.validations.empty')
      }

      return formErrors
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await updateSmartAction({
        variables: {
          id: data.id,
          messageContent: data.messageContent,
          delay: data.delay,
          emailFrom: data.emailFrom,
          applicationPhaseIds: data.atsApplicationPhaseIds,
          files: difference(data.files, currentFiles),
          deleteFiles: selectedSmartAction.files
            .filter((atsFile) => filesToRemove.includes(atsFile.file.filename))
            .map((file) => file.id),
        },
      })

      const handled = handleResult(result, (errors) => {
        setErrors(errors)
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        onClose()
      }

      return handled
    },
  })

  const [delayOptionChecked, toggleDelayOptionChecked] = useToggle(
    sendMessageOptions.some((option) => option.value === form.values.delay)
  )

  const onFiles = useUnsafeCallback(
    (files) => {
      setFilesToRemove(difference(currentFiles, files).map((file) => file.name))
      form.setFieldValue('files', files)
    },
    [form, currentFiles]
  )

  const handleOnCheckboxDisable = (event: React.ChangeEvent<HTMLInputElement>) => {
    toggleDelayOptionChecked()
    !event.target.checked && form.setFieldValue('delay', fallbackOption.value)
  }

  const onSelectPhases = (phaseId: string) => {
    form.setFieldValue('atsApplicationPhaseIds', xor(form.values.atsApplicationPhaseIds, [phaseId]))
  }

  const usedApplicationPhases = smartActions
    ?.flatMap((action) => {
      return action.atsApplicationPhases.map((phase) => phase.id)
    })
    .filter(
      (id) =>
        selectedSmartAction.atsApplicationPhases.map((phase) => phase.id).includes(id) === false
    )

  const sendAsCorporate = form.values.emailFrom !== 'conversation'

  const formErrors = pickBy(form.errors, (field) => !isEmpty(field))

  const sidepanelTitle =
    selectedSmartAction.actionType === 'reject'
      ? i18n.t('ats.job_posting_settings.smart_actions.sidepanel.rejection_emails_edit_title')
      : i18n.t('ats.job_posting_settings.smart_actions.sidepanel.phase_changed_edit_title')

  const selectPhaseLabel =
    selectedSmartAction.actionType === 'reject'
      ? i18n.t(
          'ats.job_posting_settings.smart_actions.sidepanel.rejection_emails_select_phase_label'
        )
      : i18n.t('ats.job_posting_settings.smart_actions.sidepanel.phase_changed_select_phase_label')

  return (
    <Sidepanel
      open={open}
      onClose={onClose}
      title={sidepanelTitle}
      closeLabel={i18n.t('common.close')}
      width="wide"
      footer={
        <Box flexDirection="row" gap="s16">
          <Button type="secondary" onClick={() => onClose()} disabled={loading}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={!form.isValid} onClick={() => form.handleSubmit()}>
            {i18n.t('common.update')}
          </Button>
        </Box>
      }
    >
      <Box gap="s32">
        {!!Object.keys(formErrors).length && <MissingFieldsAlert formErrors={formErrors} />}

        <Box maxWidth="s400" gap="s8">
          <Text.Normal color="textPrimary" weight="semibold">
            {selectPhaseLabel}
          </Text.Normal>

          <ApplicationPhasesSelector
            onSelectPhase={(phaseId) => onSelectPhases(String(phaseId))}
            disabledApplicationPhaseIds={usedApplicationPhases}
            checkedApplicationPhaseIds={form.values.atsApplicationPhaseIds}
            jobId={selectedSmartAction.atsJobPosting.id}
            actionType={selectedSmartAction.actionType}
          />
        </Box>

        <Box gap="s8">
          <Text.Normal color="textPrimary" weight="semibold">
            {i18n.t('ats.job_posting_settings.smart_actions.sidepanel.send_email_from')}
          </Text.Normal>

          <SelectField
            value={form.values.emailFrom}
            onChange={(_value) =>
              form.setFieldValue('emailFrom', sendAsCorporate ? 'conversation' : 'no_reply')
            }
            options={[
              {
                label: i18n.t('ats.job_posting_settings.smart_actions.sender_options.conversation'),
                value: 'conversation',
              },
              {
                label: i18n.t('ats.job_posting_settings.smart_actions.sender_options.no_reply'),
                value: 'no_reply',
              },
            ]}
          />
        </Box>

        <Box gap="s8">
          <Text.Normal color="textPrimary" weight="semibold">
            {i18n.t(
              'ats.job_posting_settings.smart_actions.sidepanel.containing_following_message'
            )}
          </Text.Normal>
          <TextEditorWithMessageTemplates
            richTextEditorRef={richTextEditorRef}
            onChange={(value) => form.setFieldValue('messageContent', value)}
            placeholder={i18n.t(
              'ats.job_posting_settings.smart_actions.sidepanel.message_placeholder'
            )}
            onFiles={onFiles}
            value={form.values.messageContent}
            initialFileState={currentFiles}
            showLabel={false}
          />
          <Box paddingTop="s8" width="full" gap="s16">
            <Box gap="s8">
              <Checkbox
                size="medium"
                label={i18n.t('ats.job_posting_settings.smart_actions.sidepanel.delay')}
                checked={delayOptionChecked}
                onChange={(event) => handleOnCheckboxDisable(event)}
              />
              <Text.Normal size="200" color="textSecondary">
                {i18n.t('ats.job_posting_settings.smart_actions.sidepanel.delay_warn')}
              </Text.Normal>
            </Box>
            <SelectField
              hideLabel
              defaultOption={defaultOption}
              readonly={!delayOptionChecked}
              options={sendMessageOptions.map((option) => ({
                label: option.label,
                value: option.value,
              }))}
              onChange={(value) => form.setFieldValue('delay', value)}
              value={form.values.delay}
            />
          </Box>
        </Box>
      </Box>
    </Sidepanel>
  )
}
